/**
 * Processing Manager
 * Handles OCR processing, text formatting, and coordinate conversion
 */

import { ApiService } from '../services/api'
import { AppState } from '../stores/app-state'
import { UIManager } from './ui-manager'
import { Toast } from '../components/toast'
import { logger } from '../utils/logger'
import { llmUsageManager } from './llm-usage-manager'

export class ProcessingManager {
  private apiService: ApiService
  private appState: AppState
  private uiManager: UIManager
  private toast: Toast
  private uiUpdateTimeout: NodeJS.Timeout | null = null

  constructor(apiService: ApiService, appState: AppState, uiManager: UIManager, toast: Toast) {
    this.apiService = apiService
    this.appState = appState
    this.uiManager = uiManager
    this.toast = toast
  }

  /**
   * Process OCR for all selections
   */
  public async processAllOCR(selections: any[]): Promise<void> {
    try {
      logger.debug('Processing OCR for all selections:', selections.length)
      
      // Process each selection
      for (const selection of selections) {
        if (!selection.extracted_text) {
          await this.processIndividualOCR(selection, false)
        }
      }
      
      logger.debug('OCR processing completed for all selections')
    } catch (error) {
      logger.error('Failed to process OCR for all selections:', error)
      // Don't throw error, just log it and continue
      console.warn('⚠️ OCR processing failed, continuing with other steps...')
    }
  }

  /**
   * Process OCR for individual selection
   */
  public async processIndividualOCR(selection: any, showProgress: boolean = true): Promise<void> {
    try {
      logger.debug('Processing OCR for selection:', selection.id)
      
      if (showProgress) {
        this.uiManager.updateLoadingModal('Processing OCR...', `Extracting text from selection ${selection.id}`)
      }

      // Check if selection already has extracted text
      if (selection.extracted_text && selection.extracted_text.trim()) {
        logger.debug('Selection already has extracted text, skipping OCR:', selection.id)
        return
      }
      
      // If selection has thumbnail_data, update it in backend to trigger auto-OCR
      if (selection.thumbnail_data && selection.thumbnail_data !== 'loading') {
        console.log('🚀 Updating selection with thumbnail to trigger backend OCR:', selection.id)
        try {
          await this.apiService.updateSelection(selection.id, {
            thumbnail_data: selection.thumbnail_data
          })
          console.log('✅ Thumbnail updated, backend OCR should process automatically')
          return // Let backend handle OCR
        } catch (error) {
          console.error('Failed to update thumbnail for OCR:', error)
        }
      }

      // Fallback: Get the PDF page image for OCR processing
      const pageImage = await this.getPageImageForSelection(selection)
      
      if (pageImage) {
        logger.debug('Got page image for OCR processing, size:', pageImage.length)
        
        // 🚀 PERFORMANCE OPTIMIZATION: Use fast OCR for better performance
        // Process OCR using the fast API with optimized timeout
        const ocrResult = await Promise.race([
          this.apiService.processFastOCR(pageImage, 'eng'),
          new Promise((_, reject) => 
            setTimeout(() => reject(new Error('OCR processing timeout')), 10000) // Reduced to 10 second timeout for fast OCR
          )
        ]) as any
        
        if (ocrResult && ocrResult.text) {
          // Update the selection with extracted text
          await this.apiService.updateSelection(selection.id, {
            extracted_text: ocrResult.text
          })
          
          // Update app state
          const selections = this.appState.getSelections()
          const updatedSelections = selections.map(sel => 
            sel.id === selection.id 
              ? { ...sel, extracted_text: ocrResult.text }
              : sel
          )
          this.appState.setSelections(updatedSelections)
          
          // Trigger graceful UI update instead of full refresh
          logger.debug('🔄 Triggering graceful UI update after OCR completion for selection:', selection.id)
          console.log('🚀 OCR COMPLETION: Dispatching graceful update for selection:', selection.id)
          
          // Use graceful update instead of aggressive re-rendering
          this.gracefulUIUpdate(selection.id)
          
          logger.debug('OCR processing completed for selection:', selection.id)
        } else {
          logger.warn('No text extracted from OCR for selection:', selection.id)
        }
      } else {
        logger.warn('No page image available for OCR processing of selection:', selection.id)
        // Skip this selection and continue with others
        return
      }
    } catch (error) {
      logger.error('Failed to process OCR for selection:', selection.id, error)
      // Don't throw error, just log it and continue with other selections
      console.warn('⚠️ OCR processing failed for selection:', selection.id, 'Continuing with other selections...')
    }
  }

  /**
   * Generate numbered list from selections
   */
  public async generateNumberedList(): Promise<void> {
    try {
      logger.debug('Generating numbered list from selections')
      console.log('🚀 ProcessingManager.generateNumberedList() called')
      
      // Check if LLM has already been used for this operation
      if (!llmUsageManager.canUseLLM('generate_list')) {
        console.log('❌ LLM already used for generate_list operation')
        this.toast.warning('Already Generated', 'Description list has already been generated for this project')
        return
      }
      
      const selections = this.appState.getSelections()
      console.log('🔍 Selections from app state:', selections?.length, selections)
      
      if (selections.length === 0) {
        logger.warn('No selections available for numbered list generation')
        console.log('❌ No selections available for numbered list generation')
        this.toast.error('No Selections', 'Please make text selections first')
        return
      }

      // Extract text from selections
      const textSnippets = selections.map(sel => sel.edited_text || sel.extracted_text || '').filter(text => text.trim())
      
      if (textSnippets.length === 0) {
        logger.warn('No text content available for numbered list generation')
        return
      }

      // Use AI to format the text into a numbered list
      const formatResult = await this.apiService.formatText(textSnippets, 'Format this text into a numbered list of property descriptions')
      
      if (formatResult.formatted_text) {
        // Save the formatted text to the project
        const currentProject = this.appState.getCurrentProject()
        if (currentProject) {
          await this.apiService.updateProjectWorkflowData(currentProject.id, {
            numbered_list_text: formatResult.formatted_text
          })
          
          // Update app state
          this.appState.setDescriptionList(formatResult.formatted_text)
          this.appState.setHasFormattedText(true)
          
          // Mark LLM usage
          llmUsageManager.markLLMUsed('generate_list')
          
          // Show success toast
          this.toast.success('List Generated', 'Description list has been created successfully')
          
          // Update the description list textarea directly instead of full page re-render
          this.updateDescriptionListTextarea(formatResult.formatted_text)
          
          logger.debug('Numbered list generated and saved successfully')
        }
      } else {
        logger.warn('Failed to generate numbered list')
        this.toast.error('Generation Failed', 'Failed to generate description list')
      }
    } catch (error) {
      logger.error('Failed to generate numbered list:', error)
      throw error
    }
  }

  /**
   * Convert numbered list to coordinates
   */
  public async convertListToCoordinates(): Promise<void> {
    try {
      logger.debug('Converting numbered list to coordinates')
      
      // Check if LLM has already been used for this operation
      if (!llmUsageManager.canUseLLM('convert_coordinates')) {
        console.log('❌ LLM already used for convert_coordinates operation')
        this.toast.warning('Already Generated', 'Coordinates have already been generated for this project')
        return
      }
      
      const descriptionList = this.appState.getDescriptionList()
      console.log('🔍 Description list from app state:', descriptionList)
      
      if (!descriptionList) {
        logger.warn('No description list available for coordinate conversion')
        this.toast.error('No Description List', 'Please generate a description list first')
        return
      }

      // Split the description list into individual descriptions
      const descriptions = descriptionList.split('\n').filter(line => line.trim())
      console.log('🔍 Split descriptions:', descriptions)
      
      if (descriptions.length === 0) {
        logger.warn('No descriptions found in the list')
        this.toast.error('Empty Description List', 'The description list is empty')
        return
      }

      // Use AI to convert descriptions to coordinates
      console.log('🎯 ProcessingManager calling convertToCoordinates with descriptions:', descriptions.length, descriptions)
      const coordinateResult = await this.apiService.convertToCoordinates(descriptions)
      console.log('🎯 ProcessingManager received coordinate result:', coordinateResult)
      
      if (coordinateResult.coordinates) {
        // Update app state with coordinates
        console.log('🎯 ProcessingManager setting coordinates:', coordinateResult.coordinates.length, coordinateResult.coordinates)
        this.appState.setCoordinates(coordinateResult.coordinates)
        
        // Save coordinates to database
        const currentProject = this.appState.getCurrentProject()
        if (currentProject) {
          const coordsText = coordinateResult.coordinates.join('\n')
          await this.apiService.updateProjectWorkflowData(currentProject.id, {
            metes_bounds_text: coordsText
          })
          console.log('💾 Saved coordinates to database:', coordsText.length, 'characters')
        }
        
        // Mark LLM usage
        llmUsageManager.markLLMUsed('convert_coordinates')
        
        // Show success toast
        this.toast.success('Coordinates Generated', 'Metes and bounds have been converted successfully')
        
        // Update the coordinate textarea directly instead of full page re-render
        this.updateCoordinateTextarea(coordinateResult.coordinates)
        
        logger.debug('Coordinates generated successfully:', coordinateResult.coordinates.length)
      } else {
        logger.warn('Failed to convert to coordinates')
        this.toast.error('Conversion Failed', 'Failed to convert descriptions to coordinates')
      }
    } catch (error) {
      logger.error('Failed to convert list to coordinates:', error)
      throw error
    }
  }

  /**
   * Update the description list textarea directly
   */
  private updateDescriptionListTextarea(text: string): void {
    const textarea = document.getElementById('description-list-textarea') as HTMLTextAreaElement
    if (textarea) {
      textarea.value = text
      // Trigger auto-resize
      textarea.style.height = 'auto'
      textarea.style.height = textarea.scrollHeight + 'px'
    }
    
    // Update the button state - disable after completion
    const generateBtn = document.getElementById('generate-description-list-btn') as HTMLButtonElement
    if (generateBtn) {
      generateBtn.innerHTML = '✅ List Generated'
      generateBtn.className = 'btn btn-secondary'
      generateBtn.disabled = true
    }
    
    // Enable the convert button
    const convertBtn = document.getElementById('convert-to-coords-btn') as HTMLButtonElement
    if (convertBtn) {
      convertBtn.disabled = false
      convertBtn.className = 'btn btn-primary'
    }
  }

  /**
   * Update the coordinate textarea directly
   */
  private updateCoordinateTextarea(coordinates: string[]): void {
    const textarea = document.getElementById('coordinate-list-textarea') as HTMLTextAreaElement
    if (textarea) {
      const coordsText = coordinates.join('\n')
      textarea.value = coordsText
      // Trigger auto-resize
      textarea.style.height = 'auto'
      textarea.style.height = textarea.scrollHeight + 'px'
    }
    
    // Update the button state - disable after completion
    const convertBtn = document.getElementById('convert-to-coords-btn') as HTMLButtonElement
    if (convertBtn) {
      convertBtn.innerHTML = '✅ Coordinates Generated'
      convertBtn.className = 'btn btn-secondary'
      convertBtn.disabled = true
    }
    
    // Update the navigation button to show "Next: Plot →"
    this.updateNavigationButton()
  }

  /**
   * Update the navigation button to show "Next: Plot →" when coordinates are available
   */
  private updateNavigationButton(): void {
    const navRight = document.querySelector('.step-navigation-bottom .nav-right')
    if (navRight) {
      const coordinates = this.appState.getCoordinates()
      const currentStep = this.appState.getCurrentStep()
      const isAdvancedMode = this.appState.getAdvancedMode()
      
      if (coordinates.length > 0) {
        // Only show "Next: Plot →" if we're not already on the plot step
        if (isAdvancedMode && currentStep === 3) {
          // Already on plot step in Advanced Mode - no next button needed
          navRight.innerHTML = ''
        } else if (!isAdvancedMode && currentStep === 4) {
          // Already on plot step in Classic Mode - no next button needed  
          navRight.innerHTML = ''
        } else {
          // Show navigation to plot step
          navRight.innerHTML = `<button class="btn btn-primary" id="step-next-bottom">Next: Plot →</button>`
        }
        
        // Re-attach the click event listener for the new button
        const nextBtn = document.getElementById('step-next-bottom')
        if (nextBtn) {
          nextBtn.addEventListener('click', (event) => {
            const target = event.target as HTMLElement
            if (target.id === 'step-next-bottom') {
              // Trigger navigation to next step
              window.dispatchEvent(new CustomEvent('navigate-to-step', { 
                detail: { step: 4 } // Plot step in Classic Mode
              }))
            }
          })
        }
      }
    }
  }

  /**
   * Process individual formatting for a selection
   */
  public async processIndividualFormatting(selection: any): Promise<void> {
    try {
      logger.debug('Processing individual formatting for selection:', selection.id)
      
      if (!selection.extracted_text) {
        logger.warn('No extracted text available for formatting:', selection.id)
        return
      }

      // Use AI to format the selection text
      const formatResult = await this.apiService.formatSelectionText(selection.id)
      
      if (formatResult.edited_text) {
        // Update the selection with formatted text
        await this.apiService.updateSelection(selection.id, {
          edited_text: formatResult.edited_text
        })
        
        // Update app state
        const selections = this.appState.getSelections()
        const updatedSelections = selections.map(sel => 
          sel.id === selection.id 
            ? { ...sel, edited_text: formatResult.edited_text }
            : sel
        )
        this.appState.setSelections(updatedSelections)
        
        logger.debug('Individual formatting completed for selection:', selection.id)
      } else {
        logger.warn('Failed to format selection:', selection.id)
      }
    } catch (error) {
      logger.error('Failed to process individual formatting for selection:', selection.id, error)
      throw error
    }
  }

  /**
   * Start background processing workflow
   */
  public async startBackgroundProcessing(): Promise<void> {
    try {
      logger.debug('Starting background processing workflow')
      
      const selections = this.appState.getSelections()
      const coordinates = this.appState.getCoordinates()
      
      // Check if processing has already been completed
      if (coordinates.length > 0) {
        logger.debug('Plot already has coordinates, skipping background processing')
        this.uiManager.hideLoadingModal()
        return
      }
      
      // DEBUG: Reset LLM usage for testing (remove in production)
      console.log('🔄 DEBUG: Resetting LLM usage for testing purposes')
      llmUsageManager.resetProjectUsage()
      
      // Step 1: Wait for auto-OCR to complete (selections are already processed in background)
      if (selections.length > 0) {
        this.uiManager.updateLoadingModal('Processing Selections...', 'Waiting for text extraction to complete')
        await this.waitForAutoOCRCompletion(selections)
      }
      
      // Step 2: Generate numbered list
      this.uiManager.updateLoadingModal('Generating descriptions...', 'Creating formatted text list')
      await this.generateNumberedList()
      
      // Step 3: Convert to coordinates
      this.uiManager.updateLoadingModal('Converting to coordinates...', 'Parsing metes and bounds')
      await this.convertListToCoordinates()
      
      // Step 4: Processing complete
      this.uiManager.updateLoadingModal('Processing complete...', 'Ready to generate plot')
      
      // Hide loading modal after a brief delay to show completion message
      setTimeout(() => {
        this.uiManager.hideLoadingModal()
        // Show success toast
        this.toast.success('Processing Complete', 'Text has been processed and converted to coordinates. Ready to generate plot!')
      }, 1500)
      
      logger.debug('Background processing workflow completed')
    } catch (error) {
      logger.error('Background processing failed:', error)
      this.uiManager.hideLoadingModal()
      throw error
    }
  }

  /**
   * Get page image for selection (helper method)
   */
  private async getPageImageForSelection(selection: any): Promise<string | null> {
    try {
      logger.debug('Getting page image for selection:', selection.id)
      
      // Check for refactored PDF viewer first, then legacy
      const pdfViewerRefactored = (window as any).pdfViewerRefactored
      const pdfViewer = (window as any).pdfViewer
      
      let canvas = null
      let currentPage = 1
      
      if (pdfViewerRefactored && pdfViewerRefactored.renderer) {
        // Use refactored viewer
        canvas = document.querySelector('#pdf-canvas') as HTMLCanvasElement
        currentPage = pdfViewerRefactored.renderer.getCurrentPage ? 
                      pdfViewerRefactored.renderer.getCurrentPage() : 1
      } else if (pdfViewer && pdfViewer.canvas) {
        // Use legacy viewer
        canvas = pdfViewer.canvas
        currentPage = pdfViewer.currentPage || 1
      }
      
      if (!canvas) {
        logger.warn('No canvas available for page image')
        return null
      }
      
      // Check if the selection is for the current page
      if (currentPage !== selection.page_number) {
        logger.debug(`Selection is on page ${selection.page_number}, but current page is ${currentPage}`)
        // For now, we'll use the current page image
        // In a full implementation, we'd need to render the specific page
      }
      
      // Convert canvas to data URL with lower quality for faster processing
      const dataURL = canvas.toDataURL('image/jpeg', 0.6) // JPEG with 0.6 quality for faster processing
      logger.debug('Successfully got page image for selection:', selection.id, 'Size:', dataURL.length)
      return dataURL
      
    } catch (error) {
      logger.error('Failed to get page image for selection:', selection.id, error)
      return null
    }
  }

  /**
   * Graceful UI update that doesn't trigger full re-renders
   */
  private gracefulUIUpdate(selectionId: string): void {
    // Clear any existing timeout
    if (this.uiUpdateTimeout) {
      clearTimeout(this.uiUpdateTimeout)
    }
    
    // Set a new timeout for the graceful UI update
    this.uiUpdateTimeout = setTimeout(() => {
      console.log('🚀 OCR COMPLETION: Dispatching graceful UI update for selection:', selectionId)
      
      // Dispatch a more specific event that won't trigger full re-renders
      window.dispatchEvent(new CustomEvent('selection-ocr-completed', { 
        detail: { source: 'ocr-completion', selectionId } 
      }))
      
      // Update generate plot button state after OCR completion
      this.updateGeneratePlotButtonState()
      
      this.uiUpdateTimeout = null
    }, 100) // 100ms debounce
  }


  /**
   * Wait for auto-OCR to complete for all selections
   */
  private async waitForAutoOCRCompletion(_selections?: any[]): Promise<void> {
    const maxWaitTime = 30000 // 30 seconds max wait
    const checkInterval = 2000 // Check every 2 seconds (reduced frequency)
    let waitTime = 0
    let lastLogTime = 0
    
    while (waitTime < maxWaitTime) {
      // Refresh selections from backend to check for OCR updates
      try {
        const currentProject = this.appState.getCurrentProject()
        if (currentProject) {
          // Force refresh from API (bypass cache)
          const response = await this.apiService.listProjectSelections(currentProject.id)
          const updatedSelections = response.selections || []
          
          // Only process if we got valid data
          if (Array.isArray(updatedSelections) && updatedSelections.length > 0) {
            // Check if any selections still need processing
            const unprocessedSelections = updatedSelections.filter(sel => !sel.extracted_text || sel.extracted_text.trim() === '')
            
            if (unprocessedSelections.length === 0) {
              console.log('✅ Auto-OCR completed for all selections')
              // Update state with OCR-completed selections
              this.appState.setSelections(updatedSelections)
              return
            }
            
            // Only log progress every 6 seconds to reduce noise
            if (waitTime - lastLogTime >= 6000) {
              console.log(`⏳ OCR Progress: ${updatedSelections.length - unprocessedSelections.length}/${updatedSelections.length} completed`)
              lastLogTime = waitTime
            }
          }
        }
      } catch (error) {
        // Only log errors once
        if (waitTime === 0) {
          console.error('Failed to refresh selections:', error)
        }
      }
      
      // Wait before next check
      await new Promise(resolve => setTimeout(resolve, checkInterval))
      waitTime += checkInterval
    }
    
    // If we get here, auto-OCR took too long
    console.warn('⚠️ Auto-OCR processing timeout after 30 seconds - proceeding with available text')
  }

  /**
   * Get processing statistics
   */
  public getProcessingStats(): {
    totalSelections: number
    processedSelections: number
    averageConfidence: number
    hasDescriptionList: boolean
    hasCoordinates: boolean
  } {
    const selections = this.appState.getSelections()
    const descriptionList = this.appState.getDescriptionList()
    const coordinates = this.appState.getCoordinates()
    
    const processedSelections = selections.filter(sel => sel.extracted_text).length
    const confidences = selections.map(sel => (sel as any).confidence_score || 0)
    const averageConfidence = confidences.length > 0 
      ? confidences.reduce((sum, conf) => sum + conf, 0) / confidences.length 
      : 0

    return {
      totalSelections: selections.length,
      processedSelections,
      averageConfidence,
      hasDescriptionList: !!(descriptionList && descriptionList.trim()),
      hasCoordinates: coordinates.length > 0
    }
  }

  /**
   * Preserve pending text edits before re-rendering
   */
  public preservePendingTextEdits(): void {
    // Save any unsaved text edits before re-rendering to prevent data loss
    const editedTextareas = document.querySelectorAll('textarea[id^="edited-text-"]') as NodeListOf<HTMLTextAreaElement>
    
    editedTextareas.forEach(textarea => {
      const selectionId = textarea.id.replace('edited-text-', '')
      const currentValue = textarea.value
      const selection = this.appState.getSelections().find(s => s.id === selectionId)
      
      if (selection && selection.edited_text !== currentValue) {
        console.log('💾 Preserving pending edit for selection:', selectionId)
        // Update app state immediately to preserve the change
        this.appState.updateSelection({ id: selectionId, edited_text: currentValue })
        
        // Also save to backend asynchronously (don't wait for it)
        this.apiService.updateSelection(selectionId, { edited_text: currentValue }).catch(error => {
          console.error('❌ Failed to save pending edit during preservation:', error)
        })
      }
    })
  }

  /**
   * Check if all selections have completed OCR processing
   */
  public areAllSelectionsProcessed(): boolean {
    const selections = this.appState.getSelections()
    if (selections.length === 0) {
      return false
    }
    
    // Check if all selections have extracted text (indicating OCR completion)
    return selections.every(selection => 
      selection.extracted_text && 
      selection.extracted_text.trim().length > 0
    )
  }

  /**
   * Update generate plot button state based on OCR processing status
   */
  public updateGeneratePlotButtonState(): void {
    const generatePlotBtn = document.getElementById('step-next-bottom') as HTMLButtonElement
    if (!generatePlotBtn) {
      return
    }

    const selections = this.appState.getSelections()
    const allProcessed = this.areAllSelectionsProcessed()
    
    if (selections.length === 0) {
      // No selections - hide button
      generatePlotBtn.style.display = 'none'
    } else if (allProcessed) {
      // All selections processed - enable button
      generatePlotBtn.style.display = 'inline-block'
      generatePlotBtn.disabled = false
      generatePlotBtn.textContent = 'Next: Generate Plot →'
      generatePlotBtn.className = 'btn btn-primary'
    } else {
      // Some selections still processing
      generatePlotBtn.style.display = 'inline-block'
      
      // In Advanced Mode, always show "Generate Plot" and allow clicking
      if (this.appState.getAdvancedMode()) {
        generatePlotBtn.disabled = false
        generatePlotBtn.textContent = 'Next: Generate Plot →'
        generatePlotBtn.className = 'btn btn-primary'
      } else {
        // Basic Mode - show processing state and disable button
        generatePlotBtn.disabled = true
        const processedCount = selections.filter(s => s.extracted_text && s.extracted_text.trim()).length
        generatePlotBtn.textContent = `⏳ Processing OCR (${processedCount}/${selections.length}) →`
        generatePlotBtn.className = 'btn btn-secondary'
      }
    }
  }

  /**
   * Start periodic OCR status checking for pending selections
   */
  public startOCRStatusPolling(): void {
    // Check for pending OCR every 2 seconds
    const checkInterval = setInterval(() => {
      const selections = this.appState.getSelections()
      const pendingSelections = selections.filter(sel => 
        sel.thumbnail_data && 
        sel.thumbnail_data !== 'loading' && 
        sel.thumbnail_data !== 'error' &&
        (sel.processed === 'pending' || sel.processed === 'processing' || !sel.extracted_text)
      )
      
      if (pendingSelections.length === 0) {
        // No pending selections, stop polling
        clearInterval(checkInterval)
        return
      }
      
      // Check if any selections have been updated with OCR results
      this.checkForOCRUpdates(pendingSelections)
    }, 2000)
    
    // Stop polling after 60 seconds to avoid infinite polling
    setTimeout(() => {
      clearInterval(checkInterval)
    }, 60000)
  }

  /**
   * Check for OCR updates from backend
   */
  private async checkForOCRUpdates(_pendingSelections: any[]): Promise<void> {
    try {
      const currentProject = this.appState.getCurrentProject()
      if (!currentProject) return
      
      // Fetch latest selections from backend
      const response = await this.apiService.listProjectSelections(currentProject.id)
      const backendSelections = response.selections || []
      
      // Check if any selections have been updated (processed status or extracted text)
      let hasUpdates = false
      const updatedSelections = this.appState.getSelections().map(frontendSelection => {
        const backendSelection = backendSelections.find(bs => bs.id === frontendSelection.id)
        if (backendSelection) {
          let updatedSelection = { ...frontendSelection }
          
          // Check for processed status changes
          if (backendSelection.processed !== frontendSelection.processed) {
            hasUpdates = true
            updatedSelection.processed = backendSelection.processed
            console.log(`🔄 Selection ${frontendSelection.id} status changed: ${frontendSelection.processed} → ${backendSelection.processed}`)
          }
          
          // Check for extracted text updates
          if (backendSelection.extracted_text && !frontendSelection.extracted_text) {
            hasUpdates = true
            updatedSelection.extracted_text = backendSelection.extracted_text
            console.log('🔄 OCR completed for selection:', frontendSelection.id)
          }
          
          return updatedSelection
        }
        return frontendSelection
      })
      
      if (hasUpdates) {
        // Update app state with OCR results using graceful updates
        for (const selection of updatedSelections) {
          const originalSelection = _pendingSelections.find((s: any) => s.id === selection.id)
          if (originalSelection && selection.extracted_text !== originalSelection.extracted_text) {
            // Use graceful update to avoid page refresh
            this.appState.updateSelectionInPlace(selection.id, {
              extracted_text: selection.extracted_text,
              processed: selection.processed
            })
            console.log('🔄 Gracefully updated OCR results for selection:', selection.id)
          }
        }
        
        // Update generate plot button state
        this.updateGeneratePlotButtonState()
        
        console.log('✅ OCR status polling found updates')
      }
    } catch (error) {
      console.error('Failed to check for OCR updates:', error)
    }
  }

  /**
   * Cleanup resources
   */
  public cleanup(): void {
    logger.debug('ProcessingManager cleanup completed')
  }
}
